import { useState, useMemo } from 'react'
import { Navigate } from 'react-router-dom'
import { useApi } from '../hooks/useApi'
import { useCurrentUser } from '../hooks/useCurrentUser'
import CostKPIs from '../components/costs/CostKPIs'
import ProjectCostCard, { type ProjectCostData } from '../components/costs/ProjectCostCard'
import ProjectCostModal from '../components/costs/ProjectCostModal'

interface CostsOverview {
  account: { balance: number; totalDeposited: number } | null
  period: string
  totalCost: number
  totalCalls: number
  projects: ProjectCostData[]
  ratesAvailable: boolean
}

export default function Costs() {
  const [selectedProject, setSelectedProject] = useState<ProjectCostData | null>(null)
  const { isAdmin, loading: userLoading } = useCurrentUser()

  const { data: data1d } = useApi<CostsOverview>('/api/costs/overview?period=1d')
  const { data: data7d } = useApi<CostsOverview>('/api/costs/overview?period=7d')
  const { data: data30d, loading, error } = useApi<CostsOverview>('/api/costs/overview?period=30d')

  const projects = data30d?.projects ?? []

  const sortedProjects = useMemo(
    () => [...projects].sort((a, b) => b.estimatedCost - a.estimatedCost),
    [projects]
  )

  // Redirect non-admin users
  if (!userLoading && !isAdmin) {
    return <Navigate to="/projects" replace />
  }

  if (loading || userLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-xl border border-red-300 bg-red-50 p-6 text-center text-red-600">
        Erreur lors du chargement des coûts : {error}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#1B2559]">Coûts API</h1>
        <p className="text-sm text-[#8B8FA7] mt-1">Suivi des coûts DataForSEO par projet et par feature</p>
      </div>

      <CostKPIs
        balance={data30d?.account?.balance ?? null}
        costToday={data1d?.totalCost ?? 0}
        cost7d={data7d?.totalCost ?? 0}
        cost30d={data30d?.totalCost ?? 0}
        callsToday={data1d?.totalCalls ?? 0}
        ratesAvailable={data30d?.ratesAvailable ?? false}
      />

      <div>
        <h2 className="mb-4 text-lg font-semibold text-[#1B2559]">Par projet</h2>
        {sortedProjects.length === 0 ? (
          <div className="bg-white border border-[#E2E4ED] rounded-xl p-8 text-center text-[#8B8FA7]">
            Aucune utilisation API enregistrée
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {sortedProjects.map((project) => (
              <ProjectCostCard
                key={project.clientId}
                project={project}
                onClick={() => setSelectedProject(project)}
              />
            ))}
          </div>
        )}
      </div>

      {selectedProject && (
        <ProjectCostModal
          project={selectedProject}
          onClose={() => setSelectedProject(null)}
        />
      )}
    </div>
  )
}
